/**
 * The quotation's own header — who it is for, who handles it, where it goes (Akhil, 2026-09-16).
 *
 * One collapsible card in place of the separate Bill-to and Vessel & delivery cards, carrying
 * every field the customer document prints: customer and their address, reference number,
 * sales person and purchaser, vessel / port / delivery location, and the currency it is priced
 * in. All of them are real `Quotation` fields and all round-trip through `update_quotation`
 * (probed on QTN-2026-00050, reverted) — except `customer_reference_date`, which is NOT on the
 * doctype yet (an ask) and not shown. Delivery time is: the doctype spells it `delivey_time`.
 *
 * Readable while collapsed: the one-line summary names the customer, reference and vessel, so
 * a quotation is never sent under a header nobody looked at.
 *
 * ---- What follows a customer change ----
 *
 * The sales person belongs to the customer (`lib/salesPersonDefault.ts`), so the server's
 * answer for the NEW customer replaces the one on the document — or clears it when it answers
 * nobody — exactly as the RFQ header card does. The address is cleared with the customer and
 * refilled from the new customer's address on file, through the same once-only fill below.
 *
 * ---- Currency ----
 *
 * Shown, not edited here. Re-pricing into another currency rewrites every line (`D33`: the server
 * derives sell from cost × margin, so the cost must be restated too), and that path — with its
 * confirmation and its rate — is the totals panel's currency picker. A bare currency field that
 * changed the label under unchanged numbers is exactly the defect that picker replaced.
 *
 * ---- Address is TEXT ----
 *
 * `Quotation.customer_address` is a Small Text (checked against the doctype 2026-09-16), not an
 * `Address` Link. The old Bill-to card wrote the `Address` docname into it, which is why the
 * server's printed document showed nothing useful. It now holds the lines themselves, filled
 * from the customer's primary address on file the first time it is seen empty, and editable.
 */

import { useEffect, useRef, useState } from 'react'
import { quotationHeaderGaps, type HeaderContext, type HeaderDrafts, type HeaderFacts } from '../../lib/quotationCompletion'
import { describeMandatory } from '../../lib/quotationMandatory'
import { Button, Card } from '../../components/ui'
import { LinkCombo } from '../../components/LinkPicker'
import { useCustomerSearch, useAddressText, useCustomerRecord } from '../../lib/api/useCustomerRecord'
import { useDeliveryLocationPicker, usePortPicker, usePurchaserPicker, useVesselName, useVesselPicker } from '../../lib/api/useEnquiryFromMail'
import { useRecommendedSalesPerson, useSalesPeople } from '../../lib/api/useMasters'
import { decideSalesPerson } from '../../lib/salesPersonDefault'
import { ENQUIRY_TYPES, type EnquiryType, type Quotation } from '../../lib/types'

export type QuotationHeaderPatch = Partial<{
  customer: string
  customer_address: string
  customer_reference_no: string
  delivery_time: string
  sales_person: string
  purchaser: string
  vessel: string
  port: string
  delivery_location: string
  order_type: string
}>

/** The doctype's `order_type` Select, verbatim (checked 2026-09-17). */
const ORDER_TYPES = ['Sales', 'Service', 'Maintenance', 'Shopping Cart'] as const

function TextField({
  label,
  value,
  placeholder,
  onCommit,
  multiline,
}: {
  label: string
  value: string
  placeholder?: string
  onCommit: (value: string) => void
  multiline?: boolean
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  const commit = () => {
    if (draft !== value) onCommit(draft)
  }
  return (
    <label className="ef">
      <span>{label}</span>
      {multiline ? (
        <textarea value={draft} rows={3} placeholder={placeholder} onChange={(e) => setDraft(e.target.value)} onBlur={commit} style={{ width: '100%', fontFamily: 'inherit' }} />
      ) : (
        <input
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
          }}
        />
      )}
    </label>
  )
}

export function QuotationHeaderCard({
  quotation,
  company,
  enquiryType,
  saving,
  onCommit,
  onCommitEnquiryType,
  onCommitAll,
}: {
  quotation: Quotation
  company: string
  /** The ENQUIRY's type — the quotation has no field of its own; editing it edits the enquiry. */
  enquiryType: EnquiryType
  saving: boolean
  onCommit: (patch: QuotationHeaderPatch) => void
  onCommitEnquiryType: (enquiryType: EnquiryType) => void
  /**
   * COMPLETION MODE (Akhil, 2026-09-19). While the quotation lacks mandatory header fields the
   * server refuses every single-field write for the others, so the card holds entries as drafts
   * and `onCommitAll` writes them in one go — to the quotation and the enquiry both. See
   * `lib/quotationCompletion.ts`.
   */
  onCommitAll: (drafts: HeaderDrafts) => void
}) {
  const [open, setOpen] = useState(false)
  const [drafts, setDrafts] = useState<HeaderDrafts>({})
  const facts: HeaderFacts = {
    company: quotation.company,
    customer: quotation.customer,
    vessel: quotation.vessel,
    port: quotation.port,
    delivery_location: quotation.delivery_location,
    sales_person: quotation.sales_person,
    purchaser: quotation.purchaser,
    customer_reference_no: quotation.customer_reference_no,
    currency: quotation.currency,
  }
  const ctx: HeaderContext = { company, enquiryType }
  const storedGaps = quotationHeaderGaps(facts, ctx, {})
  const completing = storedGaps.length > 0
  const gapsLeft = quotationHeaderGaps(facts, ctx, drafts)
  // Open on a quotation that needs completing — the fields are the only way forward.
  useEffect(() => {
    if (completing) setOpen(true)
  }, [completing])
  // Drafts are spent once the server holds them.
  useEffect(() => {
    if (!completing) setDrafts({})
  }, [completing])
  /** A field's value as shown: the draft while completing, else the document's. */
  const shown = <K extends keyof HeaderDrafts>(key: K, stored: string): string => (completing ? (drafts[key] ?? stored) : stored)
  /** A field's change: held as a draft while completing, else written at once. */
  const set = (patch: QuotationHeaderPatch) => {
    if (completing) setDrafts((d) => ({ ...d, ...(patch as HeaderDrafts) }))
    else onCommit(patch)
  }
  const useScopedCustomers = (search: string) => useCustomerSearch(search, company)
  const useScopedSalesPeople = (search: string) => useSalesPeople(quotation.customer, company, search)
  const vesselName = useVesselName(quotation.vessel || null)

  /* ---- The sales person follows a customer CHANGE made on this card ---- */
  const openedWith = useRef(quotation.customer)
  const followed = useRef<string | null>(null)
  // Never clears (2026-09-18): the field is mandatory on this very document, so a customer nobody
  // handles keeps the person already here for the purchaser to change by hand.
  const { recommended } = useRecommendedSalesPerson(quotation.customer, company)
  useEffect(() => {
    if (saving || quotation.customer === openedWith.current || followed.current === quotation.customer) return
    const next = decideSalesPerson({ current: quotation.sales_person, recommended, pairChanged: true })
    if (next === null) return
    // Deferred with cleanup, like `DerivedFiguresSync`: a mutation started inside the mount
    // effects is lost to StrictMode's simulated remount and reads as "saving" for ever.
    const timer = setTimeout(() => {
      followed.current = quotation.customer
      onCommit({ sales_person: next })
    }, 0)
    return () => clearTimeout(timer)
  }, [quotation.customer, quotation.sales_person, recommended, saving, onCommit])

  /* ---- The address on file fills an empty address, once per customer ---- */
  const record = useCustomerRecord(quotation.customer, company)
  const onFile = useAddressText(record.data?.address_name || null)
  const filledFor = useRef<string | null>(null)
  useEffect(() => {
    if (saving || quotation.customer_address !== '' || filledFor.current === quotation.customer) return
    const text = onFile.data ?? ''
    if (text === '') return
    const timer = setTimeout(() => {
      filledFor.current = quotation.customer
      onCommit({ customer_address: text })
    }, 0)
    return () => clearTimeout(timer)
  }, [quotation.customer, quotation.customer_address, onFile.data, saving, onCommit])

  const summary = [
    quotation.customer_name || quotation.customer || 'no customer',
    quotation.customer_reference_no ? `ref ${quotation.customer_reference_no}` : null,
    vesselName.data ?? (quotation.vessel || null),
    `${quotation.currency}${quotation.exchange_rate !== 1 ? ` @ ${quotation.exchange_rate}` : ''}`,
    `${enquiryType} · ${quotation.order_type || 'Sales'}`,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Card style={{ marginBottom: 14, ...(saving ? { opacity: 0.6 } : {}) }}>
      <div
        role="button"
        // Still opens when the page is read-only — see `ReadOnlyGate`.
        data-readonly-allow=""
        tabIndex={0}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((v) => !v)
          }
        }}
        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
      >
        <b>🧾 Quotation details</b>
        <span style={{ color: 'var(--muted)', fontSize: 12.5 }}>{summary}</span>
        <span style={{ flex: 1 }} />
        <span style={{ color: 'var(--muted)' }}>{open ? '▴' : '▾'}</span>
      </div>

      {open && completing ? (
        <div style={{ margin: '10px 0 6px', padding: '8px 12px', borderRadius: 8, background: 'var(--warnbg)', color: 'var(--warn)', fontSize: 12.5, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ flex: 1 }}>
            ⚠ <b>Complete the details first.</b> The server requires {describeMandatory(storedGaps)} on every quotation, and refuses each change until all are there — so fill them in below and save them together.
            {gapsLeft.length > 0 ? <> Still needed: <b>{describeMandatory(gapsLeft)}</b>.</> : <> All set — save them.</>}
          </span>
          <Button variant="primary" size="sm" disabled={gapsLeft.length > 0 || saving || Object.keys(drafts).length === 0} onClick={() => onCommitAll(drafts)}>
            {saving ? 'Saving…' : 'Save details'}
          </Button>
        </div>
      ) : null}
      {open ? (
        <div className="hdrgrid">
          <label className="ef">
            <span>Company</span>
            {/* Read-only: the enquiry's company, which every document of the enquiry shares. A
                quotation from before the field was mandatory shows the enquiry's, and the next
                write carries it (`useQuotation.ts`'s `putQuotation`). */}
            <input value={quotation.company || company} readOnly title="The enquiry's company — set on the RFQ builder header" />
          </label>
          <label className="ef">
            <span>Customer</span>
            <LinkCombo
              value={shown('customer', quotation.customer)}
              useOptions={useScopedCustomers}
              placeholder="Search customers…"
              // A new customer means a new address — cleared here so the fill above brings theirs.
              onChange={(customer) => set({ customer, customer_address: '' })}
            />
            {quotation.customer_name && quotation.customer_name !== quotation.customer ? (
              <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 3 }}>{quotation.customer_name}</div>
            ) : null}
          </label>
          <TextField
            label="Customer Ref No"
            value={shown('customer_reference_no', quotation.customer_reference_no)}
            placeholder="Their PO / enquiry number"
            onCommit={(v) => set({ customer_reference_no: v.trim() })}
          />
          <div style={{ gridColumn: '1 / -1' }}>
            <TextField
              label="Bill to — customer address (printed on the quotation)"
              value={shown('customer_address', quotation.customer_address)}
              placeholder={onFile.data ? 'No address on the quotation yet' : 'No address on file for this customer — type one, or add it in the customer master'}
              multiline
              onCommit={(customer_address) => set({ customer_address })}
            />
            {onFile.data && onFile.data !== quotation.customer_address ? (
              <Button size="sm" style={{ marginTop: 4 }} onClick={() => set({ customer_address: onFile.data ?? '' })}>
                Use the address on file
              </Button>
            ) : null}
          </div>
          <label className="ef">
            <span>Sales Person</span>
            <LinkCombo
              value={shown('sales_person', quotation.sales_person)}
              useOptions={useScopedSalesPeople}
              placeholder={quotation.customer && company ? 'Search sales people for this customer…' : 'Search sales people…'}
              onChange={(sales_person) => set({ sales_person })}
            />
          </label>
          <label className="ef">
            <span>Purchaser</span>
            <LinkCombo value={shown('purchaser', quotation.purchaser)} useOptions={usePurchaserPicker} placeholder="Search users…" onChange={(purchaser) => set({ purchaser })} />
          </label>
          <label className="ef">
            <span>Vessel</span>
            <LinkCombo value={shown('vessel', quotation.vessel)} onChange={(vessel) => set({ vessel })} useOptions={useVesselPicker} placeholder="Search by vessel name…" />
            {vesselName.data ? (
              <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 3 }}>
                {vesselName.data} · code {quotation.vessel}
              </div>
            ) : null}
          </label>
          <label className="ef">
            <span>Port of delivery</span>
            <LinkCombo value={shown('port', quotation.port)} onChange={(port) => set({ port })} useOptions={usePortPicker} placeholder="Search ports…" />
          </label>
          <label className="ef">
            <span>Delivery location</span>
            <LinkCombo
              value={shown('delivery_location', quotation.delivery_location)}
              onChange={(delivery_location) => set({ delivery_location })}
              useOptions={useDeliveryLocationPicker}
              placeholder="Search delivery locations…"
            />
          </label>
          {/* Enquiry type and order type (Akhil, 2026-09-17). The first lives on the ENQUIRY and
              is written there; the second is the quotation's own Select. */}
          <label className="ef">
            <span>Enquiry type</span>
            <select value={enquiryType} onChange={(e) => onCommitEnquiryType(e.target.value as EnquiryType)}>
              {ENQUIRY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="ef">
            <span>Order type</span>
            <select value={shown('order_type', quotation.order_type || 'Sales')} onChange={(e) => set({ order_type: e.target.value })}>
              {ORDER_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <TextField
            label="Delivery time"
            value={shown('delivery_time', quotation.delivery_time)}
            placeholder="e.g. 3 days ex-stock · on vessel arrival"
            onCommit={(v) => set({ delivery_time: v.trim() })}
          />
          <label className="ef">
            <span>Customer currency · exchange rate</span>
            <input value={`${quotation.currency} · ${quotation.exchange_rate}`} readOnly title="Changed from the totals panel's currency picker, which re-prices every line" />
            <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 3 }}>
              To quote in another currency use the currency picker on the totals panel — it re-prices every line at the rate on file.
            </div>
          </label>
          <div style={{ gridColumn: '1 / -1', fontSize: 11, color: 'var(--muted)' }}>
            Customer reference date is not on the quotation doctype yet — asked of the Frappe team.
          </div>
        </div>
      ) : null}
    </Card>
  )
}
